import { useOnValueChange } from '@calendly/hooks'
import { createContext, useContext } from 'react'
import { useHistory } from 'react-router-dom'

import { useGetOrganizationQuery, useGetPolicyQuery } from 'common'

import { selectProfileById, useFetchEventTypesQueryState, useSchedulingFetchProfilesQuery } from './api'
import { useSchedulingQueryParams } from './query_params'

const EventTypeListContext = createContext({})
const ProfileContext = createContext({})
const EventTypeContext = createContext({})

export const { Provider: EventTypeListContextProvider } = EventTypeListContext
export const { Provider: ProfileContextProvider } = ProfileContext
export const { Provider: EventTypeContextProvider } = EventTypeContext

export const useEventTypeListData = () => useContext(EventTypeListContext)

export const useProfilesData = () => {
  const history = useHistory()
  const { scopeType, scopeId } = useEventTypeListData()
  const queryParams = useSchedulingQueryParams()
  const result = useSchedulingFetchProfilesQuery({ scopeType, scopeId, queryParams })

  useOnValueChange(result.error, () => {
    if (!result.error) return

    if (result.error.type === 'policy' && scopeType !== 'my_calendly') {
      history.push('/app/scheduling/meeting_types/user/me')
    }
  })

  return result
}

/**
 * Resolves the profile row matching the current `ProfileContext` id from cached list data.
 *
 * @returns {object | undefined} Profile entity, or undefined while list data is unavailable.
 */
export const useCurrentProfile = () => {
  const { id } = useContext(ProfileContext)
  const { data } = useProfilesData()

  // Billing modals and other global Redux updates can refetch or swap the profiles cache
  // while profile rows stay mounted — guard before reading normalized `results`.
  if (!data?.results) return undefined

  return selectProfileById(data.results, id)
}

export const useCurrentPagination = () => {
  const { pagination } = useEventTypeListData()

  return pagination || { current_page: 1 }
}

export const useHasAccessToTeams = () => {
  const { data: organization } = useGetOrganizationQuery()
  const { data: policy } = useGetPolicyQuery()

  return organization.kind === 'multiple' && policy.canListTeams
}

export const useIsOnLastPage = () => {
  const { data, isFetching } = useProfilesData()

  return !isFetching
    && data
    && (!data.pagination || data.pagination.current_page >= data.pagination.total_pages)
}

export const useCurrentEventType = () => {
  const { scopeType } = useEventTypeListData()
  const { uuid, isSortable } = useContext(EventTypeContext)
  const { current_page: profilePage } = useCurrentPagination()
  const { data: setOfEventTypes } = useFetchEventTypesQueryState({ profilePage, scopeType })

  const data = setOfEventTypes?.[uuid]

  return { data, isSortable }
}
